import { InputAdornment, TextField } from '@mui/material';
import React, { useEffect } from 'react';
import { FaTimes } from 'react-icons/fa';
import { IoFilter } from 'react-icons/io5';
import SpinnerTw from '~/components/SpinnerTw';
import InfoBox from '~/shared/components/info_boxes/InfoBox';
import { Chart } from '~/shared/types/chart';
import { Tag } from '~/shared/types/tag';
import { TagCategory } from '~/shared/types/tag-category';
import { getLcn } from '~/utils/labeled-classnames';
import { useFetchTagCategories } from '../../../../../api/tag_categories_queries';
import { useFetchCharts } from '../../../api/charts_queries';
import ChartListItem from './ChartListItem';
import ChartTagCategories from './ChartTagCategories';
import ChartsTagsInCategory from './ChartsTagsInCategory';
import tagFilterLabel from './tagFilterLabel';

export type TagInCategory = {
  count: number
  tagId: number
  tag: Tag
}

export type TagsInCategory = {
  [label: string]: TagInCategory
}

export type ChartsByActiveTagCategory = {
  [label: string]: Chart[]
}

export default function ChartsList() {
  const fetchTagCategoriesQuery = useFetchTagCategories()
  const fetchChartsQuery = useFetchCharts()
  const [songTitleFilter, setSongTitleFilter] = React.useState<string>('');
  const [activeTagFilter, setActiveTagFilter] = React.useState<string>('');
  const [activeTagCategory, setActiveTagCategory] = React.useState<TagCategory | null>(null);
  const [activeTagFilterLabel, setActiveTagFilterLabel] = React.useState<string>('')

  const { data: charts } = fetchChartsQuery;
  const { data: tagCategories } = fetchTagCategoriesQuery;

  const lcn = getLcn({
    chartsList: 'flex flex-col gap-4',
  });

  useEffect(() => {
    if (fetchTagCategoriesQuery.data && fetchTagCategoriesQuery.data.length > 0) {
      setActiveTagCategory(fetchTagCategoriesQuery.data[0]);
    }
  }, [fetchTagCategoriesQuery.data]);

  if (fetchTagCategoriesQuery.isLoading || fetchChartsQuery.isLoading || !activeTagCategory) {
    return <SpinnerTw size='lg' />;
  }

  if (fetchTagCategoriesQuery.isError || fetchChartsQuery.isError) {
    return (
      <InfoBox type='error'>
        There was an error loading the charts. Please try again later.
      </InfoBox>
    )
  }

  if (
    !charts || charts.length === 0 ||
    !tagCategories || tagCategories.length === 0) {
    return (
      <InfoBox type='highlighted'>
        There are no charts to display. This is probably an error on our side. Let us know!
      </InfoBox>
    )
  }
  const tagsInActiveCategory: TagsInCategory = {}
  const chartsByActiveTagCategory: ChartsByActiveTagCategory = {}

  // loop through charts and fill tagsInActiveCategory with count of tags in active category
  activeTagCategory && charts.forEach(chart => {
    chart.tags.forEach(tag => {
      if (tag.tag_category_id !== activeTagCategory.id) return

      if (tag.name in tagsInActiveCategory) {
        tagsInActiveCategory[tag.name].count += 1;
      } else {
        tagsInActiveCategory[tag.name] = {
          count: 1,
          tagId: tag.id,
          tag,
        };
      }
    })
  })

  // loop through charts and fill chartsByActiveTagCategory
  activeTagCategory && charts.forEach(chart => {
    let hasTagInActiveCategory: boolean = false

    chart.tags.forEach(tag => {
      if (tag.tag_category_id !== activeTagCategory.id) return

      hasTagInActiveCategory = true;

      if (tag.name in chartsByActiveTagCategory) {
        chartsByActiveTagCategory[tag.name].push(chart);
      } else {
        chartsByActiveTagCategory[tag.name] = [chart];
      }
    })

    if (!hasTagInActiveCategory) {
      // zzzz prefix to push to end of list, since it means "no tag in active category"
      const noTagLabel = `zzzz Songs with no '${tagFilterLabel(activeTagCategory)}' tag`;

      if (noTagLabel in chartsByActiveTagCategory) {
        chartsByActiveTagCategory[noTagLabel].push(chart);
      } else {
        chartsByActiveTagCategory[noTagLabel] = [chart];
      }
    }
  });

  if (!activeTagCategory) return null;

  const sortedCategoryLabels = Object.keys(chartsByActiveTagCategory).sort()

  return (
    <div className={lcn.chartsList}>

      <div>

        <ChartTagCategories
          tagCategories={fetchTagCategoriesQuery.data}
          activeTagCategory={activeTagCategory}
          setActiveTagCategory={setActiveTagCategory}
          setActiveTagFilter={setActiveTagFilter}
          setActiveTagFilterLabel={setActiveTagFilterLabel}
        />

        <ChartsTagsInCategory
          activeTagCategory={activeTagCategory}
          tagsInActiveCategory={tagsInActiveCategory}
          setActiveTagFilter={setActiveTagFilter}
        />
      </div>
      <div className='grid grid-cols-1 sm:grid-cols-2 gap-3'>
        <TextField
          onChange={e => setSongTitleFilter(e.target.value)}
          placeholder='Song Title Filter'
          value={songTitleFilter}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <IoFilter />
                </InputAdornment>
              ),
              endAdornment: songTitleFilter ? (
                <InputAdornment position="end">
                  <button onClick={() => setSongTitleFilter('')}>
                    <FaTimes />
                  </button>
                </InputAdornment>
              ) : null,
            },
          }}
        />

        <TextField
          onChange={e => setActiveTagFilter(e.target.value)}
          placeholder={`${activeTagFilterLabel} Filter`}
          value={activeTagFilter}

          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <IoFilter />
                </InputAdornment>
              ),
              endAdornment: activeTagFilter ? (
                <InputAdornment position="end">
                  <button onClick={() => setActiveTagFilter('')}>
                    <FaTimes />
                  </button>
                </InputAdornment>
              ) : null,
            },
          }}
        />
      </div>


      {sortedCategoryLabels.filter(
        tagLabel => tagLabel.toLowerCase().includes(activeTagFilter.toLowerCase())
      ).map(tagLabel => {
        const tagDisplayLabel = tagLabel.startsWith('zzzz') ? tagLabel.replace('zzzz ', '') : tagLabel

        const noChartTitlesInCategoryMatch = chartsByActiveTagCategory[tagLabel].some(
          chart => chart.song_title.toLowerCase().includes(songTitleFilter.toLowerCase())
        ) === false

        if (noChartTitlesInCategoryMatch) return null

        return (
          <div key={tagLabel} className='flex flex-col gap-4'>
            <h2 className='bg-gray-700 rounded-lg px-2 py-1 text-sm '>{tagDisplayLabel} </h2>

            <div className='grid grid-cols-1 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-6 gap-4'>
              {chartsByActiveTagCategory[tagLabel].filter(
                chart => chart.song_title.toLowerCase().includes(songTitleFilter.toLowerCase())
              ).map(chart => {
                return (
                  <ChartListItem key={chart.id} chart={chart} />
                )
              })}
            </div>
          </div>
        )
      })}


    </div>
  );
}; 